import { useEffect, useState } from 'react';
import { FormControl, FormHelperText, InputLabel } from '@mui/material';
import { DatePicker as AntDatePicker } from 'antd';

import BaseFieldContainer from '../common/baseFieldContainer';
import { hasRenderableHelperText, mergeSx } from '../utils/general';
import { NotchedFieldset } from './components';
import { DATE_TIME_PICKER_CLASS_NAME, DATE_TIME_PICKER_POPUP_CLASS_NAME } from './constants';
import {
  buildDateTimePickerInputLabelSx,
  buildDateTimePickerStyles,
  buildNotchedOutlineSx,
  getPickerFormat,
} from './utils';

import type { TypographyProps } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { PanelMode } from 'rc-picker/lib/interface';
import type { FDateTimePickerProps } from './types';

const DEFAULT_TIME_CONFIG = {
  use12Hours: true,
  format: 'hh:mm A',
} as const;

function getPickerPopupClassName(popupClassName: FDateTimePickerProps['popupClassName']) {
  return popupClassName != null
    ? `${DATE_TIME_PICKER_POPUP_CLASS_NAME} ${popupClassName}`
    : DATE_TIME_PICKER_POPUP_CLASS_NAME;
}

function getPickerClassName(className: FDateTimePickerProps['className']) {
  return className != null
    ? `${DATE_TIME_PICKER_CLASS_NAME} ${className}`
    : DATE_TIME_PICKER_CLASS_NAME;
}

function FDateTimePicker(props: FDateTimePickerProps) {
  const {
    id,
    label,
    helperText,
    error = false,
    disabled = false,
    required = false,
    fullWidth = false,
    inputLabelProps,
    size = 'small',
    margin = 'none',
    color,
    sx,
    pickerStyle,
    showRequiredIndicator,
    requiredIndicatorSx,
    borderCutout = false,
    dateOnly = false,
    datePickerComponent: DatePicker = AntDatePicker,
    format,
    picker,
    onOpenChange,
    onPanelChange,
    popupClassName,
    className,
    ...rest
  } = props;

  const defaultPanelMode = picker ?? 'date';
  const [panelMode, setPanelMode] = useState<PanelMode>(defaultPanelMode);

  useEffect(() => {
    setPanelMode(defaultPanelMode);
  }, [defaultPanelMode]);

  const helperPresent = hasRenderableHelperText(helperText);
  const { sx: inputLabelUserSx, ...inputLabelRest } = inputLabelProps ?? {};
  const pickerFormat = getPickerFormat(format, dateOnly, picker);
  const pickerPopupClassName = getPickerPopupClassName(popupClassName);
  const pickerClassName = getPickerClassName(className);
  const handleOpenChange: FDateTimePickerProps['onOpenChange'] = (open) => {
    if (!open) {
      setPanelMode(defaultPanelMode);
    }

    onOpenChange?.(open);
  };
  const handlePanelChange: FDateTimePickerProps['onPanelChange'] = (value, mode) => {
    setPanelMode(mode);
    onPanelChange?.(value, mode);
  };
  const containerSx = borderCutout ? mergeSx(buildNotchedOutlineSx({ error, disabled }), sx) : sx;
  // In cutout mode the Required caption aligns to the border cutout via its defaults; the fill
  // version instead nudges it to sit over the picker's own border.
  let requiredIndicatorSxValue: TypographyProps['sx'];
  if (borderCutout) {
    requiredIndicatorSxValue = requiredIndicatorSx;
  } else {
    requiredIndicatorSxValue = mergeSx(
      (theme: Theme) => ({ right: theme.spacing(1.75), bottom: theme.spacing(-1) }),
      requiredIndicatorSx,
    );
  }

  return (
    <FormControl
      error={error}
      disabled={disabled}
      fullWidth={fullWidth}
      required={required}
      color={color}
      margin={margin}
      size={size}
      component="div"
    >
      <BaseFieldContainer
        required={required}
        showRequiredIndicator={showRequiredIndicator}
        disabled={disabled}
        helperText={helperText}
        error={error}
        fullWidth={fullWidth}
        sx={containerSx}
        requiredIndicatorSx={requiredIndicatorSxValue}
        alwaysRenderContainer
      >
        {label != null ? (
          <InputLabel
            {...inputLabelRest}
            error={error}
            disabled={disabled}
            size={size}
            required={required && !disabled ? false : required}
            shrink
            htmlFor={id}
            sx={buildDateTimePickerInputLabelSx(inputLabelUserSx, borderCutout)}
          >
            {label}
          </InputLabel>
        ) : null}
        <DatePicker
          id={id}
          className={pickerClassName}
          format={pickerFormat}
          mode={panelMode}
          picker={picker}
          onOpenChange={handleOpenChange}
          onPanelChange={handlePanelChange}
          showTime={dateOnly ? false : DEFAULT_TIME_CONFIG}
          popupClassName={pickerPopupClassName}
          disabled={disabled}
          style={{
            ...buildDateTimePickerStyles({ error, fullWidth, disabled, borderCutout }),
            ...pickerStyle,
          }}
          {...rest}
        />
        {borderCutout ? <NotchedFieldset label={label} /> : null}
      </BaseFieldContainer>
      {helperPresent ? (
        <FormHelperText component="p" sx={{ margin: 0, textAlign: 'start', width: '100%' }}>
          {helperText}
        </FormHelperText>
      ) : null}
    </FormControl>
  );
}

export default FDateTimePicker;
